import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Database, 
  Shield, 
  Building, 
  Gift, 
  PersonStanding, 
  User 
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const OperationalFlow = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Notre infrastructure à trois piliers",
      subtitle: "Comment nous préservons vos messages et contributions à travers le temps",
      step1: {
        title: "Stockage des données sécurisé",
        description: "Vos messages et souvenirs sont cryptés et stockés dans plusieurs datacenters européens certifiés ISO 27001, garantissant une redondance totale et une protection optimale contre la perte de données conformément au RGPD."
      },
      step2: {
        title: "Gestion financière réglementée",
        description: "Toutes les contributions financières sont conservées dans des banques européennes agréées ACPR, où elles sont protégées par la garantie des dépôts (100 000€) et des comptes-titres (70 000€) du FGDR, et peuvent potentiellement prendre de la valeur au fil du temps."
      },
      step3: {
        title: "Livraison à échéance",
        description: "Au moment choisi, nous accompagnons la révélation et la transmission des messages et contributions au bénéficiaire, créant ainsi un pont émotionnel unique entre le passé et le présent."
      }
    },
    en: {
      title: "Our Three-Pillar Infrastructure",
      subtitle: "How we preserve your messages and contributions through time",
      step1: {
        title: "Secure Data Storage",
        description: "Your messages and memories are encrypted and stored across multiple certified European datacenters (ISO 27001), ensuring total redundancy and optimal GDPR-compliant protection against data loss."
      },
      step2: {
        title: "Regulated Financial Management",
        description: "All financial contributions are held in ACPR-regulated European banks, where they are protected by deposit guarantees (€100,000) and securities account guarantees (€70,000) from FGDR, and can potentially gain value over time."
      },
      step3: {
        title: "Scheduled Delivery",
        description: "At the chosen moment, we facilitate the revelation and transfer of messages and contributions to the beneficiary, creating a unique emotional bridge between past and present."
      }
    },
    de: {
      title: "Unsere Drei-Säulen-Infrastruktur",
      subtitle: "Wie wir Ihre Nachrichten und Beiträge über die Zeit bewahren",
      step1: {
        title: "Sichere Datenspeicherung",
        description: "Ihre Nachrichten und Erinnerungen werden verschlüsselt und in mehreren zertifizierten europäischen Rechenzentren (ISO 27001) gespeichert, was vollständige Redundanz und DSGVO-konformen Schutz vor Datenverlust gewährleistet."
      },
      step2: {
        title: "Regulierte Finanzverwaltung",
        description: "Alle finanziellen Beiträge werden in ACPR-regulierten europäischen Banken gehalten, wo sie durch Einlagensicherung (100.000€) und Wertpapierkontenschutz (70.000€) vom FGDR geschützt sind und im Laufe der Zeit potenziell an Wert gewinnen können."
      },
      step3: {
        title: "Terminierte Lieferung",
        description: "Zum gewählten Zeitpunkt erleichtern wir die Enthüllung und Übertragung von Nachrichten und Beiträgen an den Begünstigten und schaffen so eine einzigartige emotionale Brücke zwischen Vergangenheit und Gegenwart."
      }
    }
  };

  return (
    <section className="section-padding bg-gradient-to-b from-white to-legatia-green/5">
      <div className="container mx-auto">
        <div className="text-center mb-16">
          <h2 className="section-title">
            {content[language].title}
          </h2>
          <p className="section-subtitle">
            {content[language].subtitle}
          </p>
        </div>
        
        {/* Flow diagram */}
        <div className="relative max-w-5xl mx-auto mb-24">
          {/* Connection lines */}
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-legatia-orange/30 -translate-y-1/2 hidden md:block"></div>
          <div className="absolute top-1/2 w-1 h-full bg-legatia-orange/30 -translate-y-1/2 left-1/2 -translate-x-1/2 md:hidden"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative">
            {/* Step 1 - Secure Data Storage */}
            <div className="relative">
              <Card className="h-full border-legatia-blue/20 hover:border-legatia-blue/50 transition-all shadow-md hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 bg-legatia-blue/10 rounded-full">
                      <Database className="h-10 w-10 text-legatia-blue" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center">
                    {content[language].step1.title}
                  </h3>
                  <p className="text-gray-700">
                    {content[language].step1.description}
                  </p>
                  
                  <div className="mt-6 flex justify-center">
                    <Shield className="h-6 w-6 text-legatia-green" />
                  </div>
                </CardContent>
              </Card>
              <div className="absolute top-1/2 -right-6 w-12 h-12 bg-white rounded-full border-2 border-legatia-orange z-10 flex items-center justify-center font-bold text-legatia-orange hidden md:flex">
                1
              </div>
            </div>
            
            {/* Step 2 - Financial Management */}
            <div className="relative">
              <Card className="h-full border-legatia-yellow/20 hover:border-legatia-yellow/50 transition-all shadow-md hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 bg-legatia-yellow/10 rounded-full">
                      <Building className="h-10 w-10 text-legatia-orange" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center">
                    {content[language].step2.title}
                  </h3>
                  <p className="text-gray-700">
                    {content[language].step2.description}
                  </p>
                  
                  <div className="mt-6 flex justify-center">
                    <Shield className="h-6 w-6 text-legatia-green" />
                  </div>
                </CardContent>
              </Card>
              <div className="absolute top-1/2 -right-6 w-12 h-12 bg-white rounded-full border-2 border-legatia-orange z-10 flex items-center justify-center font-bold text-legatia-orange hidden md:flex">
                2
              </div>
            </div>
            
            {/* Step 3 - Delivery */}
            <div className="relative">
              <Card className="h-full border-legatia-green/20 hover:border-legatia-green/50 transition-all shadow-md hover:shadow-lg">
                <CardContent className="p-6">
                  <div className="flex justify-center mb-6">
                    <div className="p-4 bg-legatia-green/10 rounded-full">
                      <Gift className="h-10 w-10 text-legatia-green" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-center">
                    {content[language].step3.title}
                  </h3>
                  <p className="text-gray-700">
                    {content[language].step3.description}
                  </p>
                  
                  <div className="mt-6 flex justify-center">
                    <div className="flex space-x-4">
                      <User className="h-6 w-6 text-legatia-blue" />
                      <PersonStanding className="h-6 w-6 text-legatia-orange" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <div className="absolute top-1/2 -right-6 w-12 h-12 bg-white rounded-full border-2 border-legatia-orange z-10 flex items-center justify-center font-bold text-legatia-orange hidden md:flex">
                3
              </div>
            </div>
          </div>
        </div>
        
        {/* Trust indicators */}
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-4xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <div className="mb-3 flex justify-center">
                <Shield className="h-8 w-8 text-legatia-green" />
              </div>
              <h4 className="font-bold text-lg mb-1">
                {language === 'fr' ? 'Sécurité Suisse' : 
                 language === 'en' ? 'Swiss Security' : 
                 'Schweizer Sicherheit'}
              </h4>
              <p className="text-sm text-gray-600">
                {language === 'fr' ? 'Protection des données de niveau bancaire' : 
                 language === 'en' ? 'Bank-level data protection' : 
                 'Bankenniveau-Datenschutz'}
              </p>
            </div>
            
            <div>
              <div className="mb-3 flex justify-center">
                <Building className="h-8 w-8 text-legatia-orange" />
              </div>
              <h4 className="font-bold text-lg mb-1">
                {language === 'fr' ? 'Conformité Réglementaire' : 
                 language === 'en' ? 'Regulatory Compliance' : 
                 'Regulatorische Konformität'}
              </h4>
              <p className="text-sm text-gray-600">
                {language === 'fr' ? 'Respect strict des lois financières suisses' : 
                 language === 'en' ? 'Strict adherence to Swiss financial laws' : 
                 'Strikte Einhaltung der Schweizer Finanzgesetze'}
              </p>
            </div>
            
            <div>
              <div className="mb-3 flex justify-center">
                <Gift className="h-8 w-8 text-legatia-blue" />
              </div>
              <h4 className="font-bold text-lg mb-1">
                {language === 'fr' ? 'Pérennité Garantie' : 
                 language === 'en' ? 'Guaranteed Longevity' : 
                 'Garantierte Langlebigkeit'}
              </h4>
              <p className="text-sm text-gray-600">
                {language === 'fr' ? 'Structure conçue pour durer des décennies' : 
                 language === 'en' ? 'Structure designed to last for decades' : 
                 'Struktur für jahrzehntelange Haltbarkeit'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OperationalFlow;
